// Differentiator #3: Auftrag in 3 Aktionen, nicht in 3 SAP-Reitern. (1) welches Geraet,
// (2) was ist kaputt (auto-vorgeschlagen aus frueheren Meldungen), (3) bestaetigen — alles
// uebrige kommt aus den Profil-Defaults.
//
// Portierung aus dem Vite-Prototyp (frontend/src/components/NewOrderWizard.jsx). Geaendert
// wurden NUR Styling-Idiom (v4-Token/.gx-* -> isDarkMode-Verzweigung mit konkreten
// Tailwind-v3-Klassen), Theme-/i18n-Hook, i18n-Keys (pm.-Praefix) und die Import-/API-Pfade.
// Verhalten, Props und Struktur bleiben identisch.
//
// MONOLITH-API-KONVENTION (wie SearchBar.js): `user` ist das ERSTE Argument jeder API-Funktion
// (traegt das JWT) und die Funktion liefert den geparsten JSON-Body DIREKT zurueck — KEIN
// { data }-Wrapper wie im Prototyp. Daher `res?.items` statt `({ data }) => data.items` und
// `res.ok` statt `({ data }) => data.ok`. `user` wird zusaetzlich zu `me` durchgereicht:
// `me` traegt das Profil (me.profile.scope), `user` traegt die Auth — auch an SearchBar weiter.
import React, { useEffect, useRef, useState } from 'react';
import { ArrowLeft, Check, Wrench, CheckCircle2 } from 'lucide-react';
import { useTheme } from '../../../core/ui/ThemeContext';
import { useLanguage } from '../../../core/i18n/LanguageContext';
import { equipmentNotifications, createOrder } from '../api';
import { PRIORITIES, ORDER_STEP_KEYS, SAP_SHORT_TEXT_MAX, SAP_LONG_TEXT_MAX } from '../constants';
import { Button, Card, Row, StepDots } from './ui';
import PlantSelect from './PlantSelect';
import RecentEquipmentList from './RecentEquipmentList';
import SapPayloadPanel from './SapPayloadPanel';
import SearchBar from './SearchBar';
import VoiceInput from './VoiceInput';

// Auftragsart-Label: 'pm.orderType.<code>' — fehlt die Uebersetzung (fremdes Customizing), faellt
// die Anzeige auf den rohen SAP-Code zurueck. Die Code->Absicht-Zuordnung je Kunde ist Berater-Input
// (sap-contract-requirements.md §6a); hier stehen die Demo-Codes W001/W002.
const orderTypeLabel = (t, code) => {
  const key = `pm.orderType.${code}`;
  const label = t(key);
  return label === key ? code : `${label} (${code})`;
};

function NewOrderWizard({ me, user, seedEquipment, onPlantChange, onDone, onCancel, onOpenOrder }) {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  const scope = me.profile.scope;
  const [equipment, setEquipment] = useState(seedEquipment || null);
  const [step, setStep] = useState(seedEquipment ? 2 : 1);
  const [problem, setProblem] = useState('');
  const [longText, setLongText] = useState('');
  const [priority, setPriority] = useState('3-mittel');
  const [suggestions, setSuggestions] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  // Ein Idempotency-Key pro Wizard-Session: ein Doppeltipp oder Retry darf nie ein Duplikat anlegen.
  const idemKey = useRef(crypto.randomUUID());

  // Auftragsart ist waehlbar (Details-Panel in Schritt 3), wenn das Profil mehrere erlaubt.
  // Default bleibt W002 (Reparatur — der typische "Problem melden"-Fall), sonst die erste im Scope.
  const [orderType, setOrderType] = useState(scope.orderTypes.includes('W002') ? 'W002' : scope.orderTypes[0]);
  const plant = me.activePlant;
  const workCenter = equipment?.workCenter || scope.workCenters[0];

  useEffect(() => {
    if (!equipment) return;
    let alive = true;
    // Monolith-API: `user` zuerst, Antwort ist direkt der JSON-Body (.items).
    // Vorschlaege sind optional — bei Fehlern bleibt die Chip-Zeile einfach weg.
    equipmentNotifications(user, equipment.equipmentId, me.profile.id)
      .then((res) => { if (alive) setSuggestions((res?.items || []).map((n) => n.description)); })
      .catch(() => { if (alive) setSuggestions([]); });
    return () => { alive = false; };
  }, [equipment, user, me.profile.id]);

  const submit = async () => {
    setSubmitting(true);
    const body = {
      orderType, plant, workCenter, equipmentId: equipment.equipmentId,
      description: problem.trim(), priority,
      longText: longText.trim() || undefined,
    };
    // pmCall gibt Business-Failures ({ok:false, error}) ZURUECK (Screens zeigen result.error) und
    // wirft nur bei Transport-/Serverfehlern — daher try/catch fuer den Wurf-Fall.
    try {
      const data = await createOrder(user, body, idemKey.current, me.profile.id);
      // Business-Failure: SAP hat sicher NICHTS angelegt und der Nutzer aendert vor dem Retry
      // vermutlich den Payload -> neuer Key (gleicher Key + anderer Payload verletzt den Kontrakt).
      // Bei GEWORFENEN Fehlern (Netz/Timeout) bleibt der Key stehen: SAP koennte gebucht haben,
      // genau dafuer ist der idempotente Retry da.
      if (data && data.ok === false) idemKey.current = crypto.randomUUID();
      setResult(data);
    } catch (e) {
      setResult({ ok: false, error: e?.message || 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  if (result?.ok) {
    return (
      <SuccessScreen
        result={result}
        onDone={onDone}
        onOpenOrder={onOpenOrder}
        // Mechaniker (canAddComponents) bekommen den Teile-Wortlaut, andere neutral "Auftrag oeffnen".
        openLabel={me.profile.functionRights.canAddComponents ? t('pm.order.addPartsNow') : t('pm.order.openCreated')}
      />
    );
  }

  return (
    <div>
      {/* Kopfzeile: Zurueck links (Schritt 1 -> Startseite, sonst ein Schritt), Werk rechts —
          der Auftrag wird im AKTIVEN Werk angelegt, darum ist es hier sicht- und waehlbar. */}
      <div className="flex items-center justify-between gap-3 mb-3">
        <button
          onClick={step === 1 ? onCancel : () => setStep(step - 1)}
          className={`flex items-center gap-1 font-medium ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}
        >
          <ArrowLeft size={18} /> {t('pm.common.back')}
        </button>
        <PlantSelect me={me} onPlantChange={onPlantChange} />
      </div>
      <StepDots step={step} total={3} labels={ORDER_STEP_KEYS.map((k) => t(k))} />

      {step === 1 && (
        <section className="space-y-3">
          <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
            {t('pm.order.whichDevice')}
          </h2>
          <SearchBar user={user} role={me.profile.id} autoFocus onSelect={(eq) => { setEquipment(eq); setStep(2); }} />
          {/* Zuletzt verwendete Geraete des Users (bis 10, aktives Werk) — ein Tipp statt Suche. */}
          <RecentEquipmentList user={user} me={me} max={10} onSelect={(eq) => { setEquipment(eq); setStep(2); }} />
        </section>
      )}

      {step === 2 && equipment && (
        <section className="space-y-5">
          <EquipmentBanner equipment={equipment} isDarkMode={isDarkMode} />
          <div>
            <label className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              {t('pm.order.problemLabel')}
            </label>
            <div className="mt-2">
              {/* Kurztext (SAP KTEXT, 40 Zeichen) — bewusst OHNE Mikro: Diktat gehoert in die
                  Details unten (Langtext), wo nichts gekappt wird. */}
              <textarea
                value={problem}
                onChange={(e) => setProblem(e.target.value)}
                rows={2}
                maxLength={SAP_SHORT_TEXT_MAX}
                placeholder={t('pm.order.problemPlaceholder')}
                className={`w-full outline-none p-4 text-lg rounded-xl border transition-all duration-200 ${isDarkMode
                  ? 'bg-white/[0.08] border-white/15 text-white/80 hover:bg-white/[0.12] placeholder:text-white/40'
                  : 'bg-white border-slate-200 text-slate-700 hover:border-brand/40 placeholder:text-slate-400'
                }`}
              />
            </div>
            <p className={`mt-1 text-right text-xs ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>
              {problem.length}/{SAP_SHORT_TEXT_MAX}
            </p>
            <label className={`mt-3 block font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              {t('pm.order.longTextLabel')}
            </label>
            <div className="mt-2 relative">
              {/* Langtext (Kontraktfeld longText) — HIER landet das Diktat, ohne 40-Zeichen-Kappung.
                  Ist der Kurztext noch leer, wird er aus dem ersten Diktat vorbelegt (kuerzbar). */}
              <textarea
                value={longText}
                onChange={(e) => setLongText(e.target.value)}
                rows={4}
                maxLength={SAP_LONG_TEXT_MAX}
                placeholder={t('pm.order.longTextPlaceholder')}
                className={`w-full outline-none p-4 rounded-xl border transition-all duration-200 ${isDarkMode
                  ? 'bg-white/[0.08] border-white/15 text-white/80 hover:bg-white/[0.12] placeholder:text-white/40'
                  : 'bg-white border-slate-200 text-slate-700 hover:border-brand/40 placeholder:text-slate-400'
                }`}
              />
              <VoiceInput
                user={user} role={me.profile.id} features={me.features}
                onText={(text) => {
                  setLongText((lt) => `${lt ? `${lt} ` : ''}${text}`.slice(0, SAP_LONG_TEXT_MAX));
                  setProblem((p) => p || text.slice(0, SAP_SHORT_TEXT_MAX));
                }}
              />
            </div>
            {suggestions.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                <span className={`text-sm w-full ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}>
                  {t('pm.order.pastNotifications')}
                </span>
                {suggestions.map((s, i) => (
                  // Vorschlags-Chip — soft brand-getoent (gx-chip mit brand-Akzent).
                  // slice: uebernommene Meldungstexte respektieren dieselbe Kurztext-Grenze.
                  <button
                    key={i}
                    onClick={() => setProblem(s.slice(0, SAP_SHORT_TEXT_MAX))}
                    className={`text-sm rounded-full px-3 py-1.5 border text-left transition-colors ${isDarkMode
                      ? 'bg-brand/[0.08] text-blue-200 border-brand/40 hover:bg-brand/[0.12]'
                      : 'bg-brand/[0.08] text-brand border-brand/20 hover:bg-brand/[0.12]'
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div>
            <label className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              {t('pm.order.priority')}
            </label>
            <div className="mt-2 grid grid-cols-3 gap-2">
              {PRIORITIES.map((p) => {
                const active = priority === p.key;
                // Aktiv = brand gefuellt; sonst = sekundaere Oberflaeche (gx-field-artig).
                const cls = active
                  ? (isDarkMode
                      ? 'bg-brand/20 text-blue-200 border-brand/40'
                      : 'bg-brand text-white border-brand')
                  : (isDarkMode
                      ? 'bg-white/[0.08] text-white/80 border-white/15'
                      : 'bg-slate-50 text-slate-700 border-slate-200');
                return (
                  <button
                    key={p.key}
                    onClick={() => setPriority(p.key)}
                    className={`min-h-[52px] rounded-xl font-semibold border-2 transition-all duration-200 ${cls}`}
                  >
                    {t(p.labelKey)}
                  </button>
                );
              })}
            </div>
          </div>
          <Button onClick={() => setStep(3)} disabled={!problem.trim()}>{t('pm.common.next')}</Button>
        </section>
      )}

      {step === 3 && equipment && (
        <section className="space-y-4">
          <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
            {t('pm.order.confirmTitle')}
          </h2>
          <Card className="space-y-2">
            <Row label={t('pm.order.fieldDevice')} value={`${equipment.description} (${equipment.equipmentId})`} />
            <Row label={t('pm.order.fieldProblem')} value={problem} />
            {longText.trim() && (
              <Row label={t('pm.order.fieldLongText')} value={longText.length > 80 ? `${longText.slice(0, 80)}…` : longText} />
            )}
            <Row label={t('pm.order.fieldPriority')} value={t(PRIORITIES.find((p) => p.key === priority)?.labelKey)} />
          </Card>

          {/* SAP-Transparenz: exakt der Payload, der an SAP geht — plus Auswahl der Auftragsart,
              wenn das Profil mehrere erlaubt ("Details aendern" kann jetzt wirklich aendern). */}
          <SapPayloadPanel
            showLabel={t('pm.order.detailsShow')}
            hideLabel={t('pm.order.detailsHide')}
            note={t('pm.order.defaultsNote')}
            rows={[
              { label: t('pm.order.fieldOrderType'), value: orderTypeLabel(t, orderType) },
              { label: t('pm.order.fieldPlant'), value: plant },
              { label: t('pm.order.fieldWorkCenter'), value: workCenter },
              { label: t('pm.order.fieldDevice'), value: equipment.equipmentId },
              { label: t('pm.order.fieldPriority'), value: priority },
              { label: t('pm.order.fieldLongText'), value: longText.trim() ? `${longText.trim().slice(0, 60)}${longText.trim().length > 60 ? '…' : ''}` : undefined },
            ]}
          >
            {scope.orderTypes.length > 1 && (
              <div className="pt-1">
                <span className={`text-xs font-medium ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}>
                  {t('pm.order.fieldOrderType')}
                </span>
                <div className="mt-1 grid grid-cols-2 gap-2">
                  {scope.orderTypes.map((code) => {
                    const active = orderType === code;
                    const cls = active
                      ? (isDarkMode ? 'bg-brand/20 text-blue-200 border-brand/40' : 'bg-brand text-white border-brand')
                      : (isDarkMode ? 'bg-white/[0.08] text-white/80 border-white/15' : 'bg-slate-50 text-slate-700 border-slate-200');
                    return (
                      <button
                        key={code}
                        onClick={() => setOrderType(code)}
                        className={`min-h-[40px] rounded-xl text-sm font-semibold border transition-all duration-200 ${cls}`}
                      >
                        {orderTypeLabel(t, code)}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </SapPayloadPanel>

          {result && !result.ok && (
            <p className={`text-sm ${isDarkMode ? 'text-red-300' : 'text-red-600'}`}>
              {t('pm.order.errorPrefix')}: {result.error}
            </p>
          )}
          <Button variant="confirm" onClick={submit} disabled={submitting}>
            {submitting ? t('pm.order.creating') : (<><Check /> {t('pm.order.createBtn')}</>)}
          </Button>
          <p className={`text-center text-xs ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>
            {t('pm.order.createHint')}
          </p>
        </section>
      )}
    </div>
  );
}

// Equipment-Banner — soft brand-getoente Karte mit Wrench-Badge (gx-chip/gx-card-Idiom).
function EquipmentBanner({ equipment, isDarkMode }) {
  return (
    <div className={`border rounded-2xl p-3 flex items-center gap-3 ${isDarkMode
      ? 'bg-brand/[0.08] border-brand/40'
      : 'bg-brand/[0.08] border-brand/20'
    }`}>
      <div className={`rounded-xl p-2 ${isDarkMode ? 'bg-brand/20 text-blue-200' : 'bg-brand text-white'}`}>
        <Wrench size={20} />
      </div>
      <div>
        <div className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
          {equipment.description}
        </div>
        <div className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}>
          {equipment.equipmentId} · {equipment.functionalLocation}
        </div>
      </div>
    </div>
  );
}

// Erfolgs-Screen: gruener Haken, Auftragsnummer prominent, Hinweis bei Idempotenz-Replay.
function SuccessScreen({ result, onDone, onOpenOrder, openLabel }) {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  return (
    <div className="text-center py-10 space-y-4">
      <CheckCircle2 className={`mx-auto ${isDarkMode ? 'text-emerald-300' : 'text-emerald-500'}`} size={64} />
      <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
        {t('pm.order.successTitle')}
      </h2>
      <p className={isDarkMode ? 'text-white/60' : 'text-slate-500'}>{t('pm.order.orderNumber')}</p>
      <p className={`text-3xl font-mono font-bold ${isDarkMode ? 'text-blue-200' : 'text-brand'}`}>
        {result.orderId}
      </p>
      {result.idempotent && (
        <p className={`text-sm ${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`}>
          {t('pm.order.duplicateNote')}
        </p>
      )}
      {/* Kein Sackgassen-Screen: direkter Absprung ins Auftragsdetail — dort sitzen Komponenten
          ("Teile hinzufuegen", canAddComponents) bzw. die Freigabe (Meister). */}
      <div className="pt-4 flex flex-col items-center gap-3">
        {onOpenOrder && (
          <Button onClick={() => onOpenOrder(result.orderId)}>{openLabel}</Button>
        )}
        <button
          onClick={onDone}
          className={`text-sm font-medium underline ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}
        >
          {t('pm.common.done')}
        </button>
      </div>
    </div>
  );
}

export default NewOrderWizard;
